import React, { useCallback } from 'react'

import { isEqual } from 'lodash'

import { DownloadAttachmentsButton } from 'v2/ui/components/Attribute/DownloadAttachmentsButton'
import Gallery from 'v2/ui/components/Gallery'

import { Box } from 'ui/components/Box'
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from 'ui/components/Dropdown'
import { Skeleton } from 'ui/components/Skeleton'
import { Body } from 'ui/components/Text'
import { stopPropagation } from 'ui/helpers/utilities'
import { useResponsiveValue } from 'ui/styling/helpers/useResponsiveValue'

import { useAttachmentsAttributeTextState } from './hooks/useAttachmentsAttributeTextState'
import { AttachmentValue, AttributeDisplayComponent } from './types'

type AttachmentsAttributeTextProps = React.ComponentPropsWithoutRef<typeof Body> & {
    isLoading?: boolean
}

export const AttachmentsAttributeText: AttributeDisplayComponent<
    AttachmentValue[],
    AttachmentsAttributeTextProps
> = React.memo(function AttachmentsAttributeText({
    field,
    value,
    isLoading: providedIsLoading,
    maxLength,
    maxLines,
    ...props
}) {
    const effectiveMaxLength = useResponsiveValue(maxLength)
    const effectiveMaxLines = useResponsiveValue(maxLines)

    const {
        attachments,
        isLoading,
        isOverflowPopupOpen,
        onOverflowPopupOpenChange,
        onOverflowLabelMouseEnter,
        onOverflowLabelMouseLeave,
        onOverflowLabelCloseAutoFocus,
        onOverflowLabelClick,
        wrapperRef,
        isSingle,
        label,
    } = useAttachmentsAttributeTextState({
        field,
        value,
        isLoading: providedIsLoading,
        maxLength: effectiveMaxLength,
    })

    const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault()
    }, [])

    if (!attachments?.length) {
        return null
    }

    return (
        <Box ref={wrapperRef} flex center {...props}>
            <Gallery
                center
                display="flex"
                grow
                useThumbnailsFromChildren={false}
                height="full"
                selector="a"
                style={{
                    maxWidth: '100%',
                    minWidth: 0,
                }}
                toolbarChildren={
                    <DownloadAttachmentsButton
                        attachments={attachments ?? []}
                        fieldName={field.label}
                    />
                }
            >
                {isSingle && (
                    <Box
                        minWidth={0}
                        tabIndex={0}
                        trim={!isLoading && effectiveMaxLines === 1}
                        maxWidth="full"
                        {...attachments[0].attributes}
                        role="button"
                        onClick={onOverflowLabelClick}
                    >
                        <Skeleton isLoading={isLoading} display="block">
                            <Body weight="regular" {...props}>
                                {label}
                            </Body>
                        </Skeleton>
                    </Box>
                )}

                {/* Render the gallery elements. */}
                {attachments.map((attachment) => (
                    <a
                        key={attachment.url}
                        href={attachment.url}
                        onClick={handleClick}
                        style={{
                            // Hide the element, but still keep it in the gallery.
                            display: 'none',
                        }}
                        {...attachment.attributes}
                    />
                ))}

                {!isSingle && (
                    <Skeleton isLoading={isLoading}>
                        <Dropdown
                            open={isOverflowPopupOpen}
                            onOpenChange={onOverflowPopupOpenChange}
                        >
                            <DropdownTrigger
                                asChild
                                onClick={stopPropagation}
                                onMouseEnter={onOverflowLabelMouseEnter}
                                onMouseLeave={onOverflowLabelMouseLeave}
                            >
                                <Box
                                    role="button"
                                    minWidth={0}
                                    maxWidth="full"
                                    tabIndex={0}
                                    trim={!isLoading && effectiveMaxLines === 1}
                                >
                                    <Skeleton isLoading={isLoading} display="block">
                                        <Body weight="regular" {...props}>
                                            {label}
                                        </Body>
                                    </Skeleton>
                                </Box>
                            </DropdownTrigger>
                            <DropdownContent
                                sideOffset={0}
                                side="bottom"
                                align="end"
                                onCloseAutoFocus={onOverflowLabelCloseAutoFocus}
                                onMouseEnter={onOverflowLabelMouseEnter}
                                onMouseLeave={onOverflowLabelMouseLeave}
                            >
                                {attachments.map((attachment) => (
                                    <DropdownItem
                                        key={attachment.url}
                                        label={attachment.name}
                                        onClick={onOverflowLabelClick}
                                        {...attachment.attributes}
                                    />
                                ))}
                            </DropdownContent>
                        </Dropdown>
                    </Skeleton>
                )}
            </Gallery>
        </Box>
    )
}, isEqual)
